import { type FormEvent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { accountProblem, api } from '../../api/client'
import type { AccountProblem, SignInOptions } from '../../api/types'
import { navigate } from '../../app/routing'
import { Key } from '../ui/Key'
import { type Provider, ProviderIcon } from '../ui/ProviderIcon'
import '../style/game.css'
import './door.css'

/** Where GitHub and Google send somebody back with `?problem=`. */
export const SIGN_IN_PATH = '/sign-in'
/** Where the mailed reset link lands, with `#token=`: a fragment never reaches a server or its log. */
export const RESET_PATH = '/reset'

const ART = '/stageville/art'

type Card = 'move-in' | 'come-home' | 'reset'
type Problem = AccountProblem | 'declined' | 'expired'

const HEADINGS: Record<Card, string> = { 'move-in': 'Move in', 'come-home': 'Come home', reset: 'Pick a new password' }
const SUBMITS: Record<Card, string> = { 'move-in': 'Move in', 'come-home': 'Come home', reset: 'Go home' }

const PROBLEMS: Record<Problem, string> = {
  refused: 'Stageville is not open to your address yet.',
  taken: 'Someone already lives at that address.',
  wrong: 'That email and password do not match.',
  weak: 'Make the password at least 10 characters.',
  spent: 'That link has been used or has run out.',
  email: 'That does not look like an email address.',
  declined: 'Signing in was called off. Start it again from here.',
  expired: 'That sign-in ran out of time. Start it again from here.',
  failed: 'Signing in did not go through. Try again in a moment.',
}
/** The problems whose way on is coming home, and the words that take it. */
const COME_HOME_INSTEAD: Partial<Record<Problem, string>> = { taken: 'Come home instead?', spent: 'Ask for a new one.' }
/** What GitHub and Google can send somebody back with, the only problems an address may name. */
const SENT_BACK: ReadonlySet<string> = new Set<Problem>(['refused', 'taken', 'declined', 'expired', 'failed'])

/** Not knowing what the server offers still offers the email: trying it says what went wrong, where hiding it would not. */
const EMAIL_ONLY: SignInOptions = { password: true, github: false, google: false, reset: false }

/** Neither the problem address nor the reset page is a place in the game, so signing in from them lands on `/`. */
function isDoorPath(pathname: string): boolean {
  return pathname === SIGN_IN_PATH || pathname === RESET_PATH
}

function sentBack(): Problem | null {
  if (window.location.pathname !== SIGN_IN_PATH) return null
  const problem = new URLSearchParams(window.location.search).get('problem') ?? ''
  return SENT_BACK.has(problem) ? (problem as Problem) : null
}

/** A full-page sign-in through GitHub or Google, which comes back to where the person was. */
function ServiceKey({ service, label }: { service: Provider; label: string }) {
  const { pathname, search } = window.location
  const next = isDoorPath(pathname) ? '/' : `${pathname}${search}`
  return (
    <a className="game-key" href={`/api/auth/${service}?next=${encodeURIComponent(next)}`}>
      <span className="game-key-icon door-icon">
        <ProviderIcon provider={service} />
      </span>
      {label}
    </a>
  )
}

/**
 * The first screen for somebody not signed in, over a still of a village: move
 * in, come home, a forgotten password, and the `/reset` page its mail links to.
 * `onSignedIn` reloads the session once the server has started one.
 */
export function FrontDoor({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [problem, setProblem] = useState<Problem | null>(sentBack)
  // An address GitHub or Google found already lived at comes back to coming home.
  const [card, setCard] = useState<Card>(() => {
    if (window.location.pathname === RESET_PATH) return 'reset'
    return problem === 'taken' ? 'come-home' : 'move-in'
  })
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '')
  const [options, setOptions] = useState<SignInOptions | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const emailField = useRef<HTMLInputElement>(null)

  // The token is a password for one use, so once read it leaves the address bar and the history, before the
  // backdrop or anything else is fetched with the address as its Referer. Not in the initializer above: StrictMode
  // runs that twice, and the second run would find no token.
  useLayoutEffect(() => {
    if (window.location.pathname === RESET_PATH) window.history.replaceState(null, '', RESET_PATH)
  }, [])

  useEffect(() => {
    api.signIn().then(setOptions, () => setOptions(EMAIL_ONLY))
  }, [])

  function show(next: Card) {
    if (card === 'reset') navigate('/', { replace: true })
    setCard(next)
    setProblem(null)
    setSent(null)
    setPassword('')
  }

  async function signIn(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setProblem(null)
    try {
      if (card === 'reset') await api.resetPassword(token, password)
      else await (card === 'move-in' ? api.moveIn : api.comeHome)({ email, password })
    } catch (error) {
      setProblem(accountProblem(error) ?? 'failed')
      setBusy(false)
      return
    }
    // The session says whose it is first: a reset can sign in another account than the one the game last showed.
    await onSignedIn()
    if (isDoorPath(window.location.pathname)) navigate('/', { replace: true })
  }

  async function forgot() {
    if (!emailField.current?.reportValidity()) return
    setProblem(null)
    try {
      await api.forgotPassword(email)
    } catch (error) {
      setProblem(accountProblem(error) ?? 'failed')
      return
    }
    setSent(email)
  }

  const ways = card === 'reset' ? EMAIL_ONLY : options
  const services = ways && card !== 'reset' && (ways.github || ways.google)
  const closed = ways && !ways.password && !services
  const verb = card === 'come-home' ? 'Come home' : 'Move in'

  return (
    <div className="game door">
      <picture className="door-backdrop">
        <source media="(orientation: portrait) and (max-width: 760px)" srcSet={`${ART}/front-door-phone.webp`} />
        <img src={`${ART}/front-door-1440.webp`} srcSet={`${ART}/front-door-1440.webp 1440w, ${ART}/front-door-2560.webp 2560w`} sizes="100vw" alt="" />
      </picture>
      <h1 className="door-wordmark">Stageville</h1>
      <section className="door-card game-glass" aria-labelledby="door-heading">
        <h2 id="door-heading" className="door-heading">{HEADINGS[card]}</h2>
        {card !== 'reset' && (
          <p className="door-line">{card === 'move-in' ? 'Your repositories become houses, and your agents live in them.' : 'Welcome back.'}</p>
        )}
        {closed && <p className="door-line">Sign-in opens soon.</p>}
        {ways && !closed && (
          <div className="door-ways">
            {ways.github && card !== 'reset' && <ServiceKey service="github" label={`${verb} with GitHub`} />}
            {ways.google && card !== 'reset' && <ServiceKey service="google" label={`${verb} with Google`} />}
            {services && ways.password && <p className="door-or">or with your email</p>}
            {ways.password && (
              <form className="door-ways" onSubmit={(event) => void signIn(event)}>
                {card !== 'reset' && (
                  <label className="door-field">
                    <span>Email</span>
                    <input
                      ref={emailField} type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)}
                      aria-invalid={problem === 'email'} aria-describedby={problem === 'email' ? 'door-email-problem' : undefined}
                    />
                  </label>
                )}
                {problem === 'email' && <p id="door-email-problem" className="door-note" role="alert">{PROBLEMS.email}</p>}
                <label className="door-field">
                  <span>Password</span>
                  <input type="password" autoComplete={card === 'come-home' ? 'current-password' : 'new-password'} required value={password} onChange={(event) => setPassword(event.target.value)} />
                </label>
                {card === 'come-home' && ways.reset && (
                  <button type="button" className="door-link door-forgot" onClick={() => void forgot()}>Forgot it?</button>
                )}
                {sent && <p className="door-note" role="status">We sent a link to {sent} if a house is registered to it.</p>}
                {problem && problem !== 'email' && (
                  <p className="door-note" role="alert">
                    {PROBLEMS[problem]}
                    {COME_HOME_INSTEAD[problem] && ' '}
                    {COME_HOME_INSTEAD[problem] && (card === 'come-home'
                      ? COME_HOME_INSTEAD[problem]
                      : <button type="button" className="door-link" onClick={() => show('come-home')}>{COME_HOME_INSTEAD[problem]}</button>)}
                  </p>
                )}
                <Key variant="primary" type="submit" disabled={busy}>{SUBMITS[card]}</Key>
              </form>
            )}
          </div>
        )}
        {card === 'move-in' && (
          <p className="door-switch">Already live here? <button type="button" className="door-link" onClick={() => show('come-home')}>Come home</button></p>
        )}
        {card === 'come-home' && (
          <p className="door-switch">New here? <button type="button" className="door-link" onClick={() => show('move-in')}>Move in</button></p>
        )}
        {card === 'move-in' && <p className="door-small">Moving in also opens your stagepost account, where your agents run.</p>}
      </section>
    </div>
  )
}
